import { useState, useEffect } from "react";
import { FileText, Image as ImageIcon, Trash2, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { ColorSwatch } from "@/components/ColorSwatch";
import { api, errMsg, API, pln, fmtNip } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SMS_STATUS = {
  DELIVERED: { l: "Dostarczony", c: "bg-emerald-500/20 text-emerald-300" },
  QUEUE: { l: "W kolejce", c: "bg-amber-500/20 text-amber-300" },
  SENT: { l: "Wysłany", c: "bg-sky-500/20 text-sky-300" },
  ACCEPTED: { l: "Przyjęty", c: "bg-sky-500/20 text-sky-300" },
  UNDELIVERED: { l: "Niedostarczony", c: "bg-rose-500/20 text-rose-300" },
  EXPIRED: { l: "Wygasł", c: "bg-rose-500/20 text-rose-300" },
  FAILED: { l: "Błąd", c: "bg-rose-500/20 text-rose-300" },
  ERROR: { l: "Błąd", c: "bg-rose-500/20 text-rose-300" },
  REJECTED: { l: "Odrzucony", c: "bg-rose-500/20 text-rose-300" },
};
const smsBadge = (s) => SMS_STATUS[(s || "").toUpperCase()] || { l: s || "—", c: "bg-muted text-muted-foreground" };
import { ProductionFiles } from "@/components/orders/ProductionFiles";
import { sortSizeEntries } from "@/lib/sizes";
import { queueStatus } from "@/lib/queues";

const QBadge = ({ label, done }) => (
  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold ${done ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-400/15 text-amber-300"}`}>{label}: {done ? "gotowe" : "w toku"}</span>
);

const Row = ({ k, v }) => <div className="flex justify-between gap-4 text-sm"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v || "—"}</span></div>;

export const OrderDetail = ({ order, onClose, isAdmin, onDeleted }) => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [vizMap, setVizMap] = useState({});
  useEffect(() => {
    const ids = [...new Set((order?.items || []).map((i) => i.viz_id).filter(Boolean))];
    if (!ids.length) { setVizMap({}); return; }
    api.get("/visualizations").then((r) => { const m = {}; (r.data || []).forEach((v) => { m[v.id] = v; }); setVizMap(m); }).catch(() => {});
  }, [order]);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsMsg, setSmsMsg] = useState("");
  const [smsHistory, setSmsHistory] = useState([]);
  const loadSmsHistory = () => { if (order?.id) api.get(`/orders/${order.id}/sms-history`).then((r) => setSmsHistory(r.data || [])).catch(() => {}); };
  useEffect(() => { api.get("/sms/status").then((r) => setSmsEnabled(!!r.data.enabled)).catch(() => {}); }, []);
  useEffect(() => { loadSmsHistory(); }, [order?.id]);
  const canProdFiles = ["admin", "production", "haft"].includes(user?.role);
  if (!order) return null;
  const c = order.customer, b = order.branding;
  const remove = async () => {
    if (!(await confirm({ title: "Usunąć zamówienie?", description: `Czy na pewno trwale usunąć zamówienie ${order.number}? Tej operacji nie można cofnąć.`, confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/orders/${order.id}`); toast.success("Zamówienie usunięte"); onDeleted && onDeleted(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openSms = () => {
    if (!smsEnabled) { toast.info("Integracja SMS jest nieaktywna — dodaj token SMSAPI w ustawieniach."); return; }
    setSmsPhone((order.customer?.phone) || "");
    setSmsMsg(`Zamówienie ${order.number}: status „${order.status}". W razie pytań prosimy o kontakt. Pozdrawiamy, EMPRO.`);
    setSmsOpen(true);
  };
  const sendSms = async () => {
    if (!smsMsg.trim()) return toast.error("Wpisz treść wiadomości");
    setSmsBusy(true);
    try { await api.post(`/orders/${order.id}/send-sms`, { message: smsMsg, phone: smsPhone }); toast.success("SMS wysłany do klienta"); setSmsOpen(false); loadSmsHistory(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSmsBusy(false); }
  };
  return (<>
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="order-detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-3">
            <span className="font-mono text-amber-300">{order.number}</span><StatusBadge status={order.status} />
            {isAdmin && order.wapro_produced && <span data-testid="order-detail-wapro-badge" className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[11px] font-bold text-emerald-400">WAPRO ✓</span>}
            {order.invoiced && <span data-testid="order-detail-invoiced-badge" className="inline-flex items-center gap-1 rounded-md bg-sky-500/15 px-2 py-0.5 text-[11px] font-bold text-sky-400">Faktura ✓</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Klient</p>
            <Row k="Firma" v={c.short_name || c.name} /><Row k="NIP" v={fmtNip(c.nip)} /><Row k="Kontakt" v={c.contact} />
            <Row k="Telefon" v={c.phone} /><Row k="E-mail" v={c.email} /><Row k="Adres" v={c.address} />
          </div>
          <div className="space-y-1.5">
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Zamówienie</p>
            <Row k="Handlowiec" v={order.employee_name} /><Row k="Data" v={order.created_at.slice(0, 10)} />
            <Row k="Znakowanie (podsum.)" v={b ? b.type + (b.placements?.length ? ` (${b.placements.join(", ")})` : "") : "—"} />
            {order.status === "Wysłane do produkcji" && (() => { const qs = queueStatus(order.items || []); if (!qs.hasPrint && !qs.hasHaft) return null; return (
              <div data-testid="order-queue-status" className="flex flex-wrap items-center gap-2 pt-1"><span className="text-sm text-muted-foreground">Produkcja</span>{qs.hasPrint && <QBadge label="Druk" done={qs.printDone} />}{qs.hasHaft && <QBadge label="Haft" done={qs.haftDone} />}</div>
            ); })()}
            <Row k="Netto" v={pln(order.total_net)} /><Row k="Brutto (23%)" v={pln(order.total_gross)} />
            <Row k="Prowizja" v={<span className="text-amber-300">{pln(order.total_commission)}</span>} />
          </div>
        </div>
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Produkt</th><th className="text-left px-3 py-2">Znakowanie</th><th className="text-left px-3 py-2">Rozmiary</th><th className="text-right px-3 py-2">Szt.</th><th className="text-right px-3 py-2">Cena</th><th className="text-right px-3 py-2">Prowizja/szt</th></tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-2"><div className="font-medium">{it.sku}</div><div className="flex items-center gap-1.5 text-xs text-muted-foreground"><ColorSwatch name={it.color} size={12} />{it.color}</div>{it.notes && <div className="mt-1 text-[11px] font-medium text-amber-300/90 whitespace-pre-wrap">Uwagi: {it.notes}</div>}{it.viz_id && vizMap[it.viz_id] && (<div className="mt-1 flex items-center gap-1.5" data-testid={`order-detail-viz-${i}`}>{(vizMap[it.viz_id].front_image_id || vizMap[it.viz_id].base_image_id) && <img src={`${API}/files/${vizMap[it.viz_id].front_image_id || vizMap[it.viz_id].base_image_id}`} alt="" className="h-8 w-8 rounded object-contain bg-[#0D1117]" />}<span className="text-[11px] font-medium text-sky-300">Wiz.: {vizMap[it.viz_id].title_text || vizMap[it.viz_id].title || vizMap[it.viz_id].product_name || "Wizualizacja"}</span></div>)}</td>
                  <td className="px-3 py-2 text-xs">{it.branding ? it.branding.type + (it.branding.placements?.length ? ` (${it.branding.placements.join(", ")})` : "") : "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{sortSizeEntries(it.sizes).map(([k, v]) => `${k}:${v}`).join(" ")}</td>
                  <td className="px-3 py-2 text-right">{it.quantity}</td>
                  {(() => {
                    const haftAdd = Number(it.haft_surcharge) || 0;
                    const unitTotal = (it.unit_total != null) ? Number(it.unit_total) : (Number(it.unit_price) || 0) + haftAdd;
                    return (
                      <td className="px-3 py-2 text-right" data-testid={`order-detail-price-${i}`}>
                        <div className="font-medium">{pln(unitTotal)}</div>
                        {haftAdd > 0 && <div className="text-[10px] font-medium text-amber-300/80">{pln(it.unit_price)} + {pln(haftAdd)} haft</div>}
                      </td>
                    );
                  })()}
                  <td className="px-3 py-2 text-right text-amber-300">{it.commission_note || pln(it.commission_per_piece)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {order.notes && <div className="text-sm"><p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Uwagi</p><p className="whitespace-pre-wrap">{order.notes}</p></div>}
        {order.files?.length > 0 && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Pliki graficzne</p>
            <div className="flex flex-wrap gap-2">
              {order.files.map((f) => (
                <a key={f.id} data-testid={`order-file-${f.id}`} href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-md border border-border bg-secondary px-3 py-2 text-xs hover:border-amber-400 transition-colors">
                  {f.content_type === "application/pdf" ? <FileText className="h-4 w-4 text-rose-400" /> : <img src={`${API}/files/${f.id}`} alt="" className="h-8 w-8 object-cover rounded" />}
                  {f.filename}
                </a>
              ))}
            </div>
          </div>
        )}
        {canProdFiles && (
          <div className="border-t border-border pt-3">
            <ProductionFiles order={order} canEdit={canProdFiles} showSuggestions={isAdmin} />
          </div>
        )}
        <div className="mt-4 rounded-lg border border-border bg-secondary/20 p-3" data-testid="order-sms-history">
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400/90">Historia SMS do klienta</p>
            {smsHistory.length > 0 && (
              <button data-testid="order-sms-refresh" onClick={loadSmsHistory} className="text-[10px] font-semibold text-sky-300 hover:text-sky-200 uppercase tracking-wide">Odśwież status</button>
            )}
          </div>
          {smsHistory.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nie wysłano jeszcze żadnego SMS do tego klienta.</p>
          ) : (
            <ul className="space-y-2">
              {smsHistory.map((h) => {
                const b = smsBadge(h.status);
                return (
                  <li key={h.id} data-testid={`order-sms-entry-${h.id}`} className="border-l-2 border-amber-400/50 pl-2.5 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground">{new Date(h.sent_at).toLocaleString("pl-PL")} · {h.to}</span>
                      <span data-testid={`order-sms-status-${h.id}`} className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${b.c}`}>{b.l}</span>
                    </div>
                    <div className="text-muted-foreground whitespace-pre-wrap">{h.message}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">wysłał(a): {h.by}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <Button data-testid="order-sms-open" variant="outline" onClick={openSms} className="gap-2 font-semibold">
            <MessageSquare className="h-4 w-4" />Wyślij SMS do klienta
          </Button>
          {isAdmin && (
            <Button data-testid="order-detail-delete" variant="ghost" onClick={remove} className="gap-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
              <Trash2 className="h-4 w-4" />Usuń zamówienie
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={smsOpen} onOpenChange={(o) => !o && setSmsOpen(false)}>
      <DialogContent data-testid="order-sms-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">SMS do klienta — {order.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Numer telefonu</Label><Input data-testid="order-sms-phone" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)} className="mt-1" placeholder="+48 500 100 200" /></div>
          <div><Label className="text-xs">Treść wiadomości</Label><Textarea data-testid="order-sms-message" value={smsMsg} onChange={(e) => setSmsMsg(e.target.value)} rows={4} className="mt-1" maxLength={640} /><p className="mt-1 text-[11px] text-muted-foreground">{smsMsg.length} znaków · nadawca: EMPRO · polskie znaki skracają limit pojedynczego SMS do 70.</p></div>
        </div>
        <DialogFooter>
          <Button data-testid="order-sms-send" onClick={sendSms} disabled={smsBusy} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{smsBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}Wyślij SMS</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
